/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, LogOut, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createMutationFn, createQueryOptions } from '@shared/lib/api';
import type { UserProfileResponse } from '@shared/types/settings';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '@shared/contexts/AuthContext';

const EMPTY_PROFILE: UserProfileResponse = {
  name: '',
  email: '',
  role: 'user',
  bio: '',
  avatar: '',
};

export function UserProfile() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { refreshUser, logout, user: authUser } = useAuth();
  const [profile, setProfile] = useState<UserProfileResponse>(EMPTY_PROFILE);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const {
    data: profileData,
    isLoading,
    isError,
    error,
  } = useQuery(createQueryOptions<UserProfileResponse>('/api/users/profile'));
  
  // Track whether we've detected a cache mismatch to prevent repeated invalidations
  const hasMismatchBeenHandledRef = useRef(false);
  
  // Invalidate profile cache if the authenticated user doesn't match the cached profile
  // This handles the case where user A logs out, user B logs in, and navigates to profile
  // React Query would return cached data from user A without this check
  useEffect(() => {
    // If we have both an authenticated user and cached profile data, compare them
    if (authUser?.email && profileData?.email && authUser.email !== profileData.email) {
      if (!hasMismatchBeenHandledRef.current) {
        console.log(`[UserProfile] Auth user email (${authUser.email}) doesn't match cached profile email (${profileData.email}), invalidating cache`);
        hasMismatchBeenHandledRef.current = true;
        queryClient.invalidateQueries({ queryKey: ['/api/users/profile'] });
      }
    } else if (authUser?.email && profileData?.email && authUser.email === profileData.email) {
      // Reset the flag when emails match (profile has been refreshed correctly)
      hasMismatchBeenHandledRef.current = false;
    }
  }, [authUser?.email, profileData?.email, queryClient]);

  useEffect(() => {
    if (profileData) {
      setProfile({
        name: profileData.name || '',
        email: profileData.email || '',
        role: profileData.role || 'user',
        bio: profileData.bio || '',
        avatar: profileData.avatar || '',
      });
    }
  }, [profileData]);

  const { mutate: saveProfile, isPending: isSaving } = useMutation<
    UserProfileResponse,
    Error,
    UserProfileResponse
  >({
    mutationFn: createMutationFn<UserProfileResponse, UserProfileResponse>(
      '/api/users/profile',
      'POST'
    ),
    onSuccess: async (updatedProfile) => {
      setProfile(updatedProfile);
      await queryClient.invalidateQueries({ queryKey: ['/api/users/profile'] });
      toast({
        title: 'Profile updated',
        description: 'Your profile changes have been saved successfully.',
      });
      try {
        await refreshUser();
      } catch (refreshError) {
        console.warn('Failed to refresh authenticated user after profile update:', refreshError);
      }
    },
    onError: (mutationError) => {
      toast({
        title: 'Failed to save profile',
        description: mutationError.message,
        variant: 'destructive',
      });
    },
  });

  const initials = useMemo(() => {
    if (!profile.name.trim()) {
      return 'U';
    }

    return profile.name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((segment) => segment[0]?.toUpperCase() ?? '')
      .join('') || 'U';
  }, [profile.name]);

  const handleChange = (field: keyof UserProfileResponse, value: string) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    saveProfile(profile);
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await logout();
      navigate('/signin');
    } catch (signOutError) {
      toast({
        title: 'Sign out failed',
        description: signOutError instanceof Error ? signOutError.message : 'Please try again.',
        variant: 'destructive',
      });
      setIsSigningOut(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <div
            style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              overflow: 'hidden',
              marginRight: '20px',
              background: '#d9e3f8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: 600,
              color: '#1f3a93',
            }}
          >
            {profile.avatar ? (
              <img
                src={profile.avatar}
                alt={profile.name || 'User avatar'}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              initials
            )}
          </div>
          <div>
            <h2 style={{ marginBottom: '4px' }}>Your Profile</h2>
            <p style={{ color: '#666' }}>
              Update your personal information and how it appears across Velocity PPM.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#555' }}>
            <Loader2 className="spin" size={20} />
            <span>Loading your profile...</span>
          </div>
        ) : isError ? (
          <div style={{ color: '#dc3545' }}>
            <strong>Unable to load profile.</strong>
            <div>{error instanceof Error ? error.message : 'Please try again later.'}</div>
          </div>
        ) : (
          <>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="profile-name">Full Name</label>
              <input
                id="profile-name"
                type="text"
                value={profile.name}
                onChange={(event) => handleChange('name', event.target.value)}
                placeholder="Your full name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="profile-email">Email</label>
              <input
                id="profile-email"
                type="email"
                value={profile.email}
                onChange={(event) => handleChange('email', event.target.value)}
                placeholder="your.email@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="profile-role">Role</label>
              <select
                id="profile-role"
                value={profile.role}
                onChange={(event) => handleChange('role', event.target.value)}
              >
                <option value="admin">Administrator</option>
                <option value="instructor">Instructor</option>
                <option value="curriculum_manager">Curriculum Manager</option>
                <option value="user">User</option>
                <option value="learner">Learner</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="profile-avatar">Avatar URL</label>
              <input
                id="profile-avatar"
                type="url"
                value={profile.avatar}
                onChange={(event) => handleChange('avatar', event.target.value)}
                placeholder="https://example.com/avatar.png"
              />
              <small style={{ color: '#666' }}>
                Paste a direct link to an image to update your profile avatar.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="profile-bio">Bio</label>
              <textarea
                id="profile-bio"
                rows={5}
                value={profile.bio || ''}
                onChange={(event) => handleChange('bio', event.target.value)}
                placeholder="Share a short summary about yourself, your role, and product focus areas."
                style={{ resize: 'vertical' }}
              />
            </div>

            <button
              type="submit"
              className="btn"
              disabled={isSaving}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              {isSaving ? (
                <>
                  <Loader2 size={18} className="spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Profile
                </>
              )}
            </button>
          </form>

          <div
            style={{
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid #e5e7eb',
            }}
          >
            <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>
              Account
            </h3>
            <p style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
              Sign out of your Velocity PPM account on this device.
            </p>
            <button
              type="button"
              onClick={handleSignOut}
              disabled={isSigningOut}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                backgroundColor: 'transparent',
                color: '#dc3545',
                border: '1px solid #dc3545',
                borderRadius: '6px',
                cursor: isSigningOut ? 'not-allowed' : 'pointer',
                opacity: isSigningOut ? 0.6 : 1,
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!isSigningOut) {
                  e.currentTarget.style.backgroundColor = '#dc3545';
                  e.currentTarget.style.color = '#fff';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#dc3545';
              }}
            >
              {isSigningOut ? (
                <>
                  <Loader2 size={18} className="spin" />
                  Signing out...
                </>
              ) : (
                <>
                  <LogOut size={18} />
                  Sign Out
                </>
              )}
            </button>
          </div>
          </>
        )}
      </div>
    </div>
  );
}

